import { ref, toValue, watch, type MaybeRefOrGetter } from 'vue';

export interface RegistrationFormField {
  key: string;
  type: string;
  templateOptions?: Record<string, any>;
  /** Set only when the field carries a rule; see useFieldConditions. */
  condition?: { effect: 'show' | 'require'; all: Array<Record<string, any>> } | null;
  [key: string]: any;
}

export interface RegistrationAccount {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

export type RegistrationAnswers = Record<string, any>;

/**
 * The answers a registrant is filling in, keyed by field key.
 *
 * Seeded from the form's own field list so every key exists up front — a
 * multi-checkbox starts as an empty list and everything else as null — and kept in
 * step with the signed-in account's name and email.
 */
export function useRegistrationAnswers(
  fields: MaybeRefOrGetter<RegistrationFormField[]>,
  account: MaybeRefOrGetter<RegistrationAccount | null>,
) {
  const accountAnswers = (): RegistrationAnswers => ({
    firstname: toValue(account)?.first_name,
    lastname: toValue(account)?.last_name,
    email: toValue(account)?.email,
  });

  const answers = ref<RegistrationAnswers>({
    ...toValue(fields).reduce<RegistrationAnswers>((seeded, field) => {
      seeded[field.key] = field.type === 'multi-checkbox' ? [] : null;

      return seeded;
    }, {}),
    ...accountAnswers(),
    promo_event: false,
    cgu: false,
    consent: false,
  });

  watch(() => toValue(account), () => {
    Object.assign(answers.value, accountAnswers());
  });

  /**
   * Merges a partial set of answers, for inputs that produce several at once such
   * as the DIN setting.
   */
  const merge = (partial: RegistrationAnswers): void => {
    answers.value = { ...answers.value, ...partial };
  };

  return { answers, merge };
}
